import React, { FC, useState, KeyboardEvent, useMemo } from 'react';
import { TaskItem, TaskItemDuration } from '@demind-inc/core';
import { TextField, IconButton, ClickAwayListener } from '@mui/material';
import { ArrowUpward, Remove } from '@mui/icons-material';
import { useLocalStorage } from '@uidotdev/usehooks';
import clsx from 'clsx';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { ProjectSelector, AddProjectDialog } from '../../Project';
import { CustomTimeSelector, DurationField, TaskCheckBox } from '../../common';
import { DEFAULT_TASK_DURATION_MIN, useTodoTasksContext } from '../../../data-access';
import { useProjectFilter, useAddProject, useGeneralSettings } from '../../../hooks';
import { getDefaultBoardId } from '../../../helpers';
import { getCssVariable } from '../../../utils';
import { isAppleRemindersDesktopSupported } from '../../../helpers/appleRemindersDesktop.helpers';
import './TaskCreateCard.scss';

interface TaskCreateCardProps {
  task: TaskItem;
  date: string;
  onSave: (task: TaskItem) => void | Promise<void>;
  onCancel: () => void;
}

const FONT_SIZE_XS = getCssVariable('--font-size-xs') || '12px';

const TaskCreateCard: FC<TaskCreateCardProps> = ({ task, date, onSave, onCancel }) => {
  const { t } = useTranslation();
  const { openedProjects } = useTodoTasksContext();
  const [lastUsedBoardId, setLastUsedBoardId] = useLocalStorage<string>(
    'lifestack_last_used_board_id',
    ''
  );
  const { selectedFilters, filterProjectOptions } = useProjectFilter();
  const {
    projectDialogVisible,
    isAddingProject,
    handleAddProject: handleAddProjectBase,
    handleProjectDialogVisible,
  } = useAddProject();
  const {
    generalSettings: { timeFormat },
  } = useGeneralSettings();
  const [timePickerOpen, setTimePickerOpen] = useState(false);

  const handleAddProject = async (name: string) => {
    try {
      await handleAddProjectBase(name);
      // Get the newly created project from todoProjects
      const newProject = openedProjects.find((p) => p.name === name);
      if (newProject) {
        setTaskData((prev) => ({ ...prev, boardId: newProject.todoTaskId }));
        setLastUsedBoardId(newProject.todoTaskId);
      }
    } catch (error) {
      console.error('Failed to add project:', error);
    }
  };
  // Get default project
  const defaultBoardId = getDefaultBoardId({
    selectedFilters,
    lastUsedBoardId,
    filterProjectOptions,
  });

  const defaultTaskData = useMemo(() => {
    return {
      name: '',
      boardId: defaultBoardId,
      duration: { amount: 30, unit: 'minute' } as TaskItemDuration,
      dueDateTime: { date, timezone: dayjs.tz.guess() },
    };
  }, [defaultBoardId, date]);

  const [taskData, setTaskData] = useState<TaskItem>({
    ...task,
    name: task.name || defaultTaskData.name,
    boardId: task.boardId || defaultTaskData.boardId,
    duration: task.duration || defaultTaskData.duration,
    dueDateTime: task.dueDateTime || defaultTaskData.dueDateTime,
  });

  const selectedProject = openedProjects.find((p) => p.todoTaskId === taskData?.boardId);
  const isAppleRemindersBlocked =
    selectedProject?.appFrom === 'apple-reminders' && !isAppleRemindersDesktopSupported();

  const taskDate = taskData?.dueDateTime?.date || date;

  const canSave = useMemo(() => {
    return taskData?.name?.trim() && !isAppleRemindersBlocked;
  }, [taskData?.name, isAppleRemindersBlocked]);

  const handleSave = () => {
    if (!canSave) return;
    if (isAppleRemindersBlocked) return;
    setLastUsedBoardId(taskData?.boardId || '');
    onSave(taskData);
    window.dispatchEvent(new CustomEvent('task:created'));
    setTaskData(defaultTaskData as TaskItem);
  };

  const handleProjectSelect = (boardId: string) => {
    setTaskData((prev) => ({ ...prev, boardId }));
    setLastUsedBoardId(boardId);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      onCancel();
    }
  };

  const currentDateTime = useMemo(() => {
    const dateTime = taskData?.dueDateTime?.datetime || taskData?.startDateTime?.datetime;
    return dateTime ? dayjs(dateTime) : null;
  }, [taskData?.dueDateTime?.datetime, taskData?.startDateTime?.datetime]);

  const handleTimeChange = (time: dayjs.Dayjs | null) => {
    if (time && dayjs(time).isValid()) {
      setTaskData((prev) => ({
        ...prev,
        dueDateTime: {
          timezone: dayjs.tz.guess(),
          datetime: time.toISOString(),
          date: prev.dueDateTime?.date || date,
        },
      }));
    } else {
      // Clear time but keep date if it exists
      setTaskData((prev) => {
        if (!prev.dueDateTime) return prev;
        return {
          ...prev,
          dueDateTime: {
            ...prev.dueDateTime,
            datetime: undefined,
          },
        };
      });
    }
  };

  return (
    <ClickAwayListener onClickAway={onCancel}>
      <div className="task-create-card">
        <div className="task-create-card__content">
          <div className="task-create-card__content__title-group">
            <TaskCheckBox isCompleted={false} onChange={() => void 0} disabled={true} size={24} />
            <TextField
              autoFocus
              fullWidth
              multiline
              minRows={1}
              maxRows={3}
              placeholder={t('task.quickAdd.taskTitle')}
              value={taskData?.name || ''}
              onChange={(e) => setTaskData((prev) => ({ ...prev, name: e.target.value }))}
              onKeyDown={handleKeyDown}
              variant="standard"
              InputProps={{
                disableUnderline: true,
                className: 'task-create-card__input task-create-card__input--title',
              }}
            />
          </div>

          <div className="task-create-card__chips-row">
            <div
              className={clsx(
                'task-create-card__card-pill',
                'task-create-card__time',
                !currentDateTime && 'task-create-card__card-pill--empty'
              )}
            >
              {currentDateTime || timePickerOpen ? (
                <CustomTimeSelector
                  value={currentDateTime}
                  onChange={
                    isAppleRemindersBlocked
                      ? () => void 0
                      : (time) => {
                          if (!time) {
                            handleTimeChange(null);
                            return;
                          }
                          const combined = dayjs(taskDate).hour(time.hour()).minute(time.minute());
                          handleTimeChange(combined);
                        }
                  }
                  open={timePickerOpen}
                  setOpen={setTimePickerOpen}
                  size="small"
                  amPm={timeFormat === '12h'}
                  showClearButton={!isAppleRemindersBlocked && !!currentDateTime}
                  fontWeight="bold"
                  disabled={isAppleRemindersBlocked}
                  defaultTimeInSelector={dayjs()}
                  className="task-create-card__time-selector"
                />
              ) : (
                <p
                  className="task-create-card__time-selector__placeholder"
                  onClick={() => setTimePickerOpen(true)}
                >
                  {t('task.quickAdd.time')}
                </p>
              )}
            </div>
            <DurationField
              value={taskData?.duration || { amount: DEFAULT_TASK_DURATION_MIN, unit: 'minute' }}
              onChange={(duration) => setTaskData((prev) => ({ ...prev, duration }))}
              showIcon={false}
              textStyle={{
                fontSize: FONT_SIZE_XS,
                fontWeight: '600',
              }}
              className="task-create-card__card-pill"
              disabled={isAppleRemindersBlocked}
              shortFormat
            />
          </div>

          <div className="task-create-card__footer">
            <ProjectSelector
              projects={openedProjects}
              selectedProject={openedProjects.find((p) => p.todoTaskId === taskData?.boardId)}
              onProjectSelect={handleProjectSelect}
              enableToAdd
              onProjectAdd={() => handleProjectDialogVisible(true)}
              isDisabledProject={(p) =>
                p.appFrom === 'apple-reminders' && !isAppleRemindersDesktopSupported()
              }
              disabledProjectTooltip={t('task.project.appleRemindersDesktopOnly')}
              className={clsx('task-create-card__project-selector')}
            />
            <div className="task-create-card__action-buttons">
              <IconButton
                onClick={onCancel}
                className="task-create-card__action-btn task-create-card__action-btn--cancel"
                size="small"
              >
                <Remove fontSize="small" />
              </IconButton>

              <IconButton
                onClick={handleSave}
                disabled={!canSave}
                className={clsx(
                  'task-create-card__action-btn',
                  'task-create-card__action-btn--create'
                )}
                size="small"
              >
                <ArrowUpward fontSize="small" />
              </IconButton>
            </div>
          </div>
        </div>

        <AddProjectDialog
          isAdding={isAddingProject}
          visible={projectDialogVisible}
          existingProjectNames={openedProjects.map((p) => p.name)}
          onAdd={handleAddProject}
          onClose={() => handleProjectDialogVisible(false)}
        />
      </div>
    </ClickAwayListener>
  );
};

export default TaskCreateCard;
